import { isAxiosError } from "axios";

type ApiErrorBody = {
  success?: boolean;
  message?: string;
  error?: string | { code?: string; message?: string };
  errors?: Array<{ message?: string } | string> | Record<string, string[]>;
};

function readNestedErrorMessage(error: ApiErrorBody["error"]): string | undefined {
  if (!error) return undefined;
  if (typeof error === "string") {
    const trimmed = error.trim();
    return trimmed || undefined;
  }
  const message = error.message?.trim();
  return message || undefined;
}

function readErrorsMessage(errors: ApiErrorBody["errors"]): string | undefined {
  if (!errors) return undefined;

  if (Array.isArray(errors)) {
    for (const item of errors) {
      if (typeof item === "string" && item.trim()) return item.trim();
      if (item && typeof item === "object" && item.message?.trim()) {
        return item.message.trim();
      }
    }
    return undefined;
  }

  for (const messages of Object.values(errors)) {
    const first = messages?.find((message) => message?.trim());
    if (first?.trim()) return first.trim();
  }

  return undefined;
}

/** Extract a user-facing message from an API response body. */
export function getApiErrorMessageFromData(
  data: unknown,
  fallback = "Something went wrong. Please try again.",
): string {
  if (!data || typeof data !== "object") return fallback;

  const body = data as ApiErrorBody;
  const nested = readNestedErrorMessage(body.error);
  if (nested) return nested;

  const topLevel = body.message?.trim();
  if (topLevel) return topLevel;

  const fromErrors = readErrorsMessage(body.errors);
  if (fromErrors) return fromErrors;

  return fallback;
}

/** Extract a user-facing message from an Axios/unknown error. */
export function getApiErrorMessage(
  error: unknown,
  fallback = "Something went wrong. Please try again.",
): string {
  if (isAxiosError(error)) {
    return getApiErrorMessageFromData(error.response?.data, fallback);
  }

  if (error instanceof Error && error.message.trim()) {
    return error.message.trim();
  }

  return fallback;
}
